/*
Description: Reads the Raintree Scheduler for Sync Cal.
Raintree exposes no schedule endpoint the browser can call on its own (RT Web
talks to the server over its own RPC), so the appointments are read off the
React fiber props that render the scheduler grid: Widget.ActiveDate plus
Widget.Columns[].Appointments.
Date: 2026-09-18
*/

type ProviderHint = { providerId?: string; providerName?: string };

// Raintree status codes. Only cancellations change what we do; the rest are
// passed through for the backend to store.
const CANCELLED_STATUSES = ['C'];

export async function getSchedule(hint: ProviderHint = {}) {
  // The fiber expandos live in the page's world, so the content script asks
  // main_world.js for them; the direct read covers a main-world caller.
  const widget = findSchedulerWidget() || (await requestSchedulerWidget());
  if (!widget) {
    throw new Error(
      'Open your schedule in Raintree, then click Sync Cal again.'
    );
  }

  const date = formatActiveDate(widget.ActiveDate);
  if (!date) {
    throw new Error('Could not read the date on your Raintree schedule.');
  }

  const provider = pickProvider(widget, hint);
  const rows = provider.appointments.map((appointment: any) =>
    mapAppointment(appointment, provider.title, date)
  );

  return {
    date,
    appointments: rows.filter((row: any) => row && !row.skipped),
    // Rows we could not turn into a visit, so the panel can name them.
    skipped: rows.filter((row: any) => row && row.skipped)
  };
}

function requestSchedulerWidget(): Promise<any> {
  const reqId = `raintree_schedule_${Date.now()}`;
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, 2000);

    const handler = (event: MessageEvent) => {
      if (
        event.source === window &&
        event.data?.action === 'getRaintreeSchedule_response' &&
        event.data.reqId === reqId
      ) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', handler);
        resolve(event.data.result || null);
      }
    };

    window.addEventListener('message', handler);
    window.postMessage({ action: 'getRaintreeSchedule', reqId }, '*');
  });
}

// The scheduler mounts one .scheduler-column per provider; the widget holding
// the day's data lives further up the fiber tree.
function findSchedulerWidget(): any {
  const element = document.querySelector('.scheduler-column, .appointment');
  if (!element) return null;

  const fiberKey = Object.keys(element).find(
    (k) =>
      k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
  );
  if (!fiberKey) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let fiber = (element as any)[fiberKey];
  while (fiber) {
    const widget = fiber.memoizedProps?.Widget;
    if (widget?.Columns && widget?.ActiveDate) return widget;
    fiber = fiber.return;
  }
  return null;
}

function formatActiveDate(activeDate: any): string | null {
  const year = Number(activeDate?.Year);
  const month = Number(activeDate?.Month);
  const day = Number(activeDate?.Day);
  if (!year || !month || !day) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

// The grid view gives one column per provider. The single-provider ("PROV")
// view gives one column per field — Patient, Length, Phone… — all carrying the
// same DocCode, with the appointments on the first. Grouping by DocCode reads
// both layouts the same way.
function providerGroups(widget: any) {
  const groups: any[] = [];
  const byCode = new Map<string, any>();

  for (const column of widget.Columns || []) {
    if (!column || column.IsComment) continue;
    const code = String(column.DocCode || column.Title || '');
    let group = byCode.get(code);
    if (!group) {
      group = { code, title: column.Title, appointments: [] };
      byCode.set(code, group);
      groups.push(group);
    }
    group.appointments.push(...(column.Appointments || []));
  }

  return groups;
}

// The grid shows every provider at the location, so syncing the wrong one would
// create another therapist's visits. Match on the user's Raintree provider code
// first, then on their name, and only fall through to the single provider case
// — otherwise ask them to filter the schedule.
function pickProvider(widget: any, hint: ProviderHint) {
  const groups = providerGroups(widget);
  if (!groups.length) {
    throw new Error('No providers found on your Raintree schedule.');
  }

  // One provider on screen: the widget names them, the columns name fields.
  if (groups.length === 1) {
    return { ...groups[0], title: widget.Title || groups[0].title };
  }

  const providerId = String(hint.providerId || '')
    .trim()
    .toLowerCase();
  if (providerId) {
    const byCode = groups.find(
      (group) => group.code.toLowerCase() === providerId
    );
    if (byCode) return byCode;
  }

  const byName = groups.filter((group) =>
    titleMatchesProvider(group.title, hint.providerName)
  );
  if (byName.length === 1) return byName[0];

  const titles = groups
    .map((group) => group.title)
    .filter(Boolean)
    .join(', ');
  throw new Error(
    `Could not tell which schedule is yours (${titles}). Filter the Raintree scheduler to your own schedule, then click Sync Cal again.`
  );
}

// Column titles carry credentials ("Drew Barton PT, DPT"), so every token of
// the user's name has to appear in the title rather than the two matching.
// A first name on its own counts — plenty of profiles only have one — and the
// caller keeps it honest by using this only when exactly one column matches.
function titleMatchesProvider(title: string, providerName?: string): boolean {
  const tokens = nameTokens(providerName).filter((token) => token.length > 1);
  if (!tokens.length) return false;
  const titleTokens = new Set(nameTokens(title));
  return tokens.every((token) => titleTokens.has(token));
}

function nameTokens(value?: string): string[] {
  return (
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .match(/[a-z]+/g) || []
  );
}

function mapAppointment(appointment: any, provider: string, date: string) {
  const scheduledAt = toTimestamp(date, appointment?.StartTime);
  const { firstName, lastName } = parsePatientName(
    firstField(appointment?.LeftValues, appointment?.LeftDisplay)
  );
  if (!firstName || !lastName) {
    return {
      skipped: true,
      scheduledAt,
      reason: "couldn't read the patient name"
    };
  }

  const status = String(appointment?.Status || '').toUpperCase();
  return {
    appointmentId: appointment?.Guid ? String(appointment.Guid) : undefined,
    firstName,
    lastName,
    // The scheduler carries no DOB or patient number, so the EMR id is
    // synthesized the same way NewRaintreeInject.getPatientInfo does it —
    // a synced visit and a visit opened from the chart land on one patient.
    emrId: `${lastName},${firstName}`.replace(/\s/g, ''),
    date,
    scheduledAt,
    noteType:
      firstField(appointment?.RightValues, appointment?.RightDisplay) ||
      undefined,
    provider: provider || undefined,
    status: CANCELLED_STATUSES.includes(status)
      ? 'cancelled'
      : status || undefined
  };
}

// The single-provider view renders one line per configured column — patient,
// length, phone, email, type, comment — as a CRLF-joined string, with the same
// values split out in LeftValues/RightValues. The grid view carries the one
// value only. Either way the field we want is the first.
function firstField(values: any, display: any): string {
  const raw =
    Array.isArray(values) && values.length
      ? values[0]
      : String(display || '').split(/\r?\n/)[0];
  return stripHTML(raw);
}

// LeftDisplay is the scheduler's display template, rendered as HTML —
// "<b>First Last</b>" by default, "Last, First" where a clinic configures it
// that way.
function parsePatientName(leftDisplay: string) {
  const text = stripHTML(leftDisplay);
  if (!text) return { firstName: '', lastName: '' };

  if (text.includes(',')) {
    const [last, first] = text.split(',', 2);
    return { firstName: clean(first), lastName: clean(last) };
  }

  // Same naive split as getPatientInfo so both paths agree on the EMR id.
  const [first, last] = text.split(' ');
  return { firstName: clean(first), lastName: clean(last) };
}

function stripHTML(value: string): string {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function clean(value?: string): string {
  return String(value || '').trim();
}

// Raintree keeps appointment times as minutes from midnight, in clinic-local
// time. Conversations are stored and read back as UTC (the app parses
// created_at with a trailing Z), so convert — same as the WebPT sync does.
function toTimestamp(date: string, startTime: any): string | undefined {
  const minutes = Number(startTime);
  if (!Number.isFinite(minutes) || minutes < 0) return undefined;
  const [year, month, day] = date.split('-').map(Number);
  const start = new Date(
    year,
    month - 1,
    day,
    Math.floor(minutes / 60),
    minutes % 60
  );
  if (Number.isNaN(start.getTime())) return undefined;
  return start.toISOString().slice(0, -1);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
